/**
 * ADR-0240 Slice 2c — typed client for the account-white-label endpoints.
 *
 *   GET  /v1/account/white-label  — read the current row (404
 *                                    `not_set` → `{ kind: 'not_set' }`).
 *   PUT  /v1/account/white-label  — upsert the agency's row. 400
 *                                    branches surface the specific
 *                                    reason so the dashboard can
 *                                    render inline validation copy
 *                                    (invalid_logo_url, invalid_hex,
 *                                    invalid_display_name).
 *
 * Shape mirrors `apps/dashboard/src/api/agency-referral.ts`.
 */

import { ApiError, type HttpClient } from './http-client.js';

/** Agency Portal v3 §5.4 — chosen CTA on branded reports. */
export type ReportCtaMode = 'claim_sill' | 'custom' | 'none';

export interface WhiteLabelWire {
  readonly display_name: string;
  readonly logo_url: string | null;
  readonly accent_hex: string | null;
  /** Agency Portal v2 §7.4 — https:// or mailto: URL for the agency
   *  "Talk to {Agency}" CTA on branded reports. `null` → report
   *  CTA falls back to the quieter "reply to the email" line. */
  readonly contact_url: string | null;
  /** Agency Portal v3 §5.4 — null = default behaviour (contact_url
   *  present → `custom`, else `none`). */
  readonly report_cta_mode: ReportCtaMode | null;
  readonly updated_at: string;
}

export type GetWhiteLabelResult =
  | { readonly kind: 'ok'; readonly state: WhiteLabelWire }
  | { readonly kind: 'not_set' }
  | { readonly kind: 'forbidden'; readonly reason: string }
  | { readonly kind: 'error'; readonly message: string };

export async function getWhiteLabel(http: HttpClient): Promise<GetWhiteLabelResult> {
  try {
    const res = await http.request<WhiteLabelWire>({
      method: 'GET',
      path: '/v1/account/white-label',
    });
    if (res === undefined) return { kind: 'error', message: 'Empty response body.' };
    return { kind: 'ok', state: res };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.kind === 'not_found') return { kind: 'not_set' };
      if (err.kind === 'forbidden') {
        return { kind: 'forbidden', reason: err.server_reason ?? 'forbidden' };
      }
    }
    return { kind: 'error', message: explainApiError(err) };
  }
}

export interface PutWhiteLabelInput {
  readonly display_name: string;
  readonly logo_url: string | null;
  readonly accent_hex: string | null;
  readonly contact_url: string | null;
  /** Agency Portal v3 §5.4 — chosen CTA mode. `undefined` on the
   *  wire preserves the stored value; `null` clears it. */
  readonly report_cta_mode?: ReportCtaMode | null;
}

export type PutWhiteLabelResult =
  | { readonly kind: 'ok'; readonly state: PutWhiteLabelInput }
  | { readonly kind: 'invalid_display_name'; readonly reason: string }
  | { readonly kind: 'invalid_logo_url'; readonly reason: string }
  | { readonly kind: 'invalid_accent_hex'; readonly reason: string }
  | { readonly kind: 'invalid_contact_url'; readonly reason: string }
  | { readonly kind: 'invalid_cta_mode'; readonly reason: string }
  | { readonly kind: 'forbidden'; readonly reason: string }
  | { readonly kind: 'error'; readonly message: string };

export async function putWhiteLabel(
  http: HttpClient,
  input: PutWhiteLabelInput,
): Promise<PutWhiteLabelResult> {
  try {
    // Agency Portal v3 §5.4 — only ship `report_cta_mode` when the
    // caller provided it (undefined preserves the stored value on
    // the server; null clears it).
    const body: Record<string, unknown> = {
      display_name: input.display_name,
      logo_url: input.logo_url,
      accent_hex: input.accent_hex,
      contact_url: input.contact_url,
    };
    if ('report_cta_mode' in input) {
      body['report_cta_mode'] = input.report_cta_mode ?? null;
    }
    const res = await http.request<PutWhiteLabelInput>({
      method: 'PUT',
      path: '/v1/account/white-label',
      body,
    });
    if (res === undefined) return { kind: 'error', message: 'Empty response body.' };
    return { kind: 'ok', state: res };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.kind === 'bad_request') {
        const reason = err.server_reason ?? '';
        // Agency Portal v3 §5.2 — carry the specific `reserved_brand`
        // reason through so the dashboard can render the "name is too
        // close to a protected brand" copy inline rather than the
        // generic "invalid characters" message.
        if (reason === 'reserved_brand' || reason === 'invalid_display_name') {
          return { kind: 'invalid_display_name', reason };
        }
        if (reason === 'invalid_logo_url') {
          return { kind: 'invalid_logo_url', reason };
        }
        if (reason === 'invalid_accent_hex') {
          return { kind: 'invalid_accent_hex', reason };
        }
        if (reason === 'invalid_cta_mode' || reason === 'not_string') {
          return { kind: 'invalid_cta_mode', reason };
        }
        if (reason === 'invalid_contact_url'
            || /^(not_string|too_long|forbidden_scheme|invalid_shape)$/.test(reason)) {
          return { kind: 'invalid_contact_url', reason };
        }
      }
      if (err.kind === 'forbidden') {
        return { kind: 'forbidden', reason: err.server_reason ?? 'forbidden' };
      }
    }
    return { kind: 'error', message: explainApiError(err) };
  }
}

/**
 * Agency Portal v2 §4.7 — render the real branded email HTML with
 * the unsaved form state. Returns an opaque HTML string the
 * dashboard renders inside a sandboxed iframe (`sandbox=""`,
 * `srcdoc`). Non-mutating; server is authoritative on validation.
 */
export type PreviewEmailResult =
  | { readonly kind: 'ok'; readonly html: string }
  | { readonly kind: 'invalid' }
  | { readonly kind: 'rate_limited'; readonly retry_after_seconds: number }
  | { readonly kind: 'forbidden'; readonly reason: string }
  | { readonly kind: 'error'; readonly message: string };

export async function previewWhiteLabelEmail(
  http: HttpClient,
  input: {
    readonly display_name: string;
    readonly logo_url: string | null;
    readonly accent_hex?: string | null;
    readonly contact_url?: string | null;
    /** Agency Portal v3 §5.4 — surface the chosen CTA mode in the
     *  preview iframe so the agency sees the picked button. */
    readonly report_cta_mode?: ReportCtaMode | null;
  },
): Promise<PreviewEmailResult> {
  try {
    // The server returns text/html; use requestBlob so the client
    // doesn't try to JSON.parse the body (JSON parse would reject
    // the HTML string). Converting to text in the caller lets the
    // dashboard pipe the result straight into an iframe srcdoc.
    const blob = await http.requestBlob({
      method: 'POST',
      path: '/v1/account/white-label/preview-email',
      body: {
        display_name: input.display_name,
        logo_url: input.logo_url,
        accent_hex: input.accent_hex ?? null,
        contact_url: input.contact_url ?? null,
        report_cta_mode: input.report_cta_mode ?? null,
      },
    });
    const html = await blob.text();
    return { kind: 'ok', html };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.kind === 'bad_request') return { kind: 'invalid' };
      if (err.kind === 'rate_limited') {
        return { kind: 'rate_limited', retry_after_seconds: 60 };
      }
      if (err.kind === 'forbidden') {
        return { kind: 'forbidden', reason: err.server_reason ?? 'forbidden' };
      }
    }
    return { kind: 'error', message: explainApiError(err) };
  }
}

function explainApiError(err: unknown): string {
  if (err instanceof ApiError) {
    return err.server_reason ?? err.kind;
  }
  return err instanceof Error ? err.message : 'unknown_error';
}
